import { useState, useMemo, useCallback, useEffect } from 'react';
import { safeStorage } from '@/lib/safeStorage';
import { ArrowLeft, Sparkles, MapPin, Loader2 } from 'lucide-react';
import type { RankedPlace } from '@/hooks/useLLM';
import type { SavedItinerary } from '@/hooks/useItineraries';
import { useLanguage } from '@/hooks/useLanguage';
import { POICard } from './POICard';
import { VibeFilter } from './VibeFilter';

import type { FindMoreStage } from '@/hooks/usePlaces';

interface ResultsScreenProps {
  places: RankedPlace[];
  location: string;
  timeAvailable: string;
  transport: string;
  vibes: string[];
  timezone?: string;
  onBack: () => void;
  onSaveItinerary: (selectedPlaces: RankedPlace[]) => Promise<SavedItinerary | null>;
  onInstantSave?: (place: RankedPlace) => Promise<string | null>;
  onInstantRemove?: (placeId: string) => Promise<boolean>;
  onOpenSettings?: () => void;
  onLoadMore?: () => Promise<RankedPlace[]>;
  findMoreStage?: FindMoreStage;
  loadingMore?: boolean;
  isAuthenticated?: boolean;
  onOpenSavedTrips?: () => void;
  onSaveSuccess?: () => void;
  savedPlaceIds?: Set<string>;
  onSavedPlaceIdsChange?: (ids: Set<string>) => void;
  referralCode?: string | null;
}

const TRANSPORT_ICONS: Record<string, string> = {
  walking: '🚶',
  transit: '🚇',
  taxi: '🚕'
};

const FIND_MORE_HINTS: Record<FindMoreStage, string> = {
  0: '',
  1: 'Searching nearby quadrants…',
  2: 'Trying broader place categories',
  3: 'Including all ratings',
  4: 'Adding currently-closed places',
  5: ''
};

export function ResultsScreen({
  places,
  location,
  timeAvailable,
  transport,
  vibes,
  timezone,
  onBack,
  onSaveItinerary,
  onInstantSave,
  onInstantRemove,
  onLoadMore,
  findMoreStage = 0,
  loadingMore,
  isAuthenticated,
  onSaveSuccess,
  savedPlaceIds: savedPlaceIdsProp,
  onSavedPlaceIdsChange,
  referralCode
}: ResultsScreenProps) {
  const { t } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<string>>(new Set());
  const [localSavedPlaceIds, setLocalSavedPlaceIds] = useState<Set<string>>(new Set());
  const savedPlaceIds = savedPlaceIdsProp ?? localSavedPlaceIds;
  const setSavedPlaceIds = onSavedPlaceIdsChange
    ? (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
        const next = typeof updater === 'function' ? updater(savedPlaceIds) : updater;
        onSavedPlaceIdsChange(next);
      }
    : setLocalSavedPlaceIds;
  const [savedToastId, setSavedToastId] = useState<string | null>(null);
  const [activeVibes, setActiveVibes] = useState<Set<string>>(() => new Set(vibes));
  const [expandedPlaceId, setExpandedPlaceId] = useState<string | null>(null);
  const [showCoachMark, setShowCoachMark] = useState(false);

  useEffect(() => {
    if (isAuthenticated && onInstantSave && !safeStorage.getItem('instantSave_coached')) {
      setShowCoachMark(true);
    }
  }, [isAuthenticated, onInstantSave]);

  const showLoadMoreButton = findMoreStage < 5 && onLoadMore;

  const filteredPlaces = useMemo(() => {
    // Filter by active vibes but preserve the insertion order from the places array.
    // The initial batch is already ranked by the LLM; find-more batches are appended
    // at the end. Re-sorting would reorder existing items and reset scroll position.
    //
    // Scuba bug safety net: if every active filter is a custom vibe, treat the
    // filter as a no-op. Custom vibes come in as prefixed strings (`custom:scuba`)
    // but the LLM may echo them back bare, causing strict-set lookups to drop
    // everything. useLLM normalizes, this is belt-and-braces.
    const activeVibesArr = Array.from(activeVibes);
    const allActiveAreCustom = activeVibesArr.length > 0 && activeVibesArr.every(v => v.startsWith('custom:'));
    return places.filter(place => {
      const placeVibes = Array.isArray(place.matchingVibes)
        ? place.matchingVibes.filter(v => v && v.trim())
        : [];
      if (placeVibes.length === 0) return true;
      if (allActiveAreCustom) return true;
      return placeVibes.some(vibe => {
        if (activeVibes.has(vibe)) return true;
        // Loose match: custom:X ↔ X
        if (activeVibes.has(`custom:${vibe}`)) return true;
        if (vibe.startsWith('custom:') && activeVibes.has(vibe.slice(7))) return true;
        return false;
      });
    });
  }, [places, activeVibes]);

  const handleVibeToggle = (vibe: string) => {
    setActiveVibes(prev => {
      const newSet = new Set(prev);

      if (newSet.has(vibe)) {
        if (newSet.size === 1) {
          const otherVibes = vibes.filter(v => v !== vibe);
          if (otherVibes.length > 0) {
            newSet.delete(vibe);
            newSet.add(otherVibes[0]);
          }
        } else {
          newSet.delete(vibe);
        }
      } else {
        newSet.add(vibe);
      }

      return newSet;
    });
  };

  const togglePlaceSelection = useCallback((placeId: string) => {
    // For authenticated users with instant save enabled
    if (isAuthenticated && onInstantSave && onInstantRemove) {
      if (savedPlaceIds.has(placeId)) {
        // Optimistic remove: update UI immediately, then persist
        setSavedPlaceIds(prev => { const s = new Set(prev); s.delete(placeId); return s; });
        onInstantRemove(placeId).then(success => {
          if (!success) {
            // Revert on failure
            setSavedPlaceIds(prev => new Set(prev).add(placeId));
          }
        });
      } else {
        // Optimistic save: update UI immediately, then persist
        if (showCoachMark) {
          setShowCoachMark(false);
          safeStorage.setItem('instantSave_coached', '1');
        }
        const place = places.find(p => p.id === placeId);
        if (!place) return;
        setSavedPlaceIds(prev => new Set(prev).add(placeId));
        setSavedToastId(placeId);
        setTimeout(() => setSavedToastId(prev => prev === placeId ? null : prev), 1500);
        onInstantSave(place).then(tripId => {
          if (!tripId) {
            // Revert on failure
            setSavedPlaceIds(prev => { const s = new Set(prev); s.delete(placeId); return s; });
          }
        });
      }
    } else {
      // Not authenticated: just toggle selection (existing behaviour)
      setSelectedPlaceIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(placeId)) {
          newSet.delete(placeId);
        } else {
          newSet.add(placeId);
        }
        return newSet;
      });
    }
  }, [isAuthenticated, onInstantSave, onInstantRemove, savedPlaceIds, places]);

  const handleSaveItinerary = async () => {
    if (selectedPlaceIds.size === 0) return;

    setIsSaving(true);
    try {
      const selectedPlaces = places.filter(p => selectedPlaceIds.has(p.id));
      const saved = await onSaveItinerary(selectedPlaces);
      if (saved) {
        setSelectedPlaceIds(new Set());
        if (onSaveSuccess) {
          onSaveSuccess();
        }
      }
    } catch (error) {
      console.error('Failed to save itinerary:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const transportEmoji = TRANSPORT_ICONS[transport] || '🚶';
  const timeLabel = t.time[timeAvailable as keyof typeof t.time] || timeAvailable;

  const getVibeLabel = (vibe: string) => {
    if (vibe.startsWith('custom:')) return vibe.slice(7);
    return t.vibes[vibe as keyof typeof t.vibes] || vibe;
  };

  const getFilterStatusText = () => {
    const showingAll = activeVibes.size === vibes.length;
    if (showingAll) {
      return t.results.showingAll || 'Showing All';
    }
    const activeVibeLabels = vibes
      .filter(v => activeVibes.has(v))
      .map(v => getVibeLabel(v))
      .join(', ');
    const hiddenCount = places.length - filteredPlaces.length;
    if (hiddenCount === 0) {
      return `${activeVibeLabels}: ${filteredPlaces.length} places`;
    }
    return `${activeVibeLabels}: ${filteredPlaces.length} of ${places.length} places`;
  };

  const handleViewTrip = () => {
    if (onSaveSuccess) {
      onSaveSuccess();
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <div className="sticky top-0 z-10 bg-[var(--bg-primary)]/95 backdrop-blur-sm border-b border-[var(--border)]">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onBack}
              aria-label="Go back"
              className="w-10 h-10 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <p className="text-xl font-semibold tracking-tight text-[var(--text-primary)]">Trip<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boom</span></p>

            <div className="px-3 py-1.5 rounded-full bg-[var(--bg-elevated)] text-xs text-[var(--text-secondary)]">
              {location} · {timeLabel}
            </div>
          </div>

          <div className="text-xs text-[var(--text-secondary)] text-center mb-3">
            {t.results.placesFound.replace('{count}', String(places.length))} · {transportEmoji} {t.transport[transport as keyof typeof t.transport]}
            {vibes.length > 1 && (
              <span className="text-[var(--accent-teal)]"> · {getFilterStatusText()}</span>
            )}
          </div>

          {vibes.length > 1 && (
            <VibeFilter
              vibes={vibes}
              activeVibes={activeVibes}
              onToggle={handleVibeToggle}
            />
          )}

          {places.some(p => p.source === 'offline') && (
            <p role="status" className="mt-3 px-3 py-2 rounded-lg bg-[var(--accent-amber)]/10 border border-[var(--accent-amber)]/30 text-xs text-[var(--text-secondary)] text-center">
              {t.results.offlineRankingBanner}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-36">
        <div className="space-y-4">
          {filteredPlaces.map((place, index) => (
            <div key={place.id} className={`relative ${index < 3 ? `animate-fade-in-delay-${index}` : 'animate-fade-in-delay-3'}`}>
              <POICard
                place={place}
                transport={transport}
                timezone={timezone}
                referralCode={referralCode}
                isSelected={selectedPlaceIds.has(place.id) || savedPlaceIds.has(place.id)}
                isSaved={savedPlaceIds.has(place.id)}
                onToggleSelect={togglePlaceSelection}
                isExpanded={expandedPlaceId === place.id}
                onToggleExpand={(id) => setExpandedPlaceId(expandedPlaceId === id ? null : id)}
              />
              {savedToastId === place.id && (
                <div role="status" aria-live="polite" className="absolute top-2 left-14 z-10 px-3 py-1 rounded-lg bg-black/80 text-white text-sm font-medium animate-fade-in backdrop-blur-sm">
                  {t.results.saved}
                </div>
              )}
              {showCoachMark && index === 0 && (
                <div
                  className="absolute top-1 left-14 z-20 animate-fade-in cursor-pointer"
                  onClick={() => {
                    setShowCoachMark(false);
                    safeStorage.setItem('instantSave_coached', '1');
                  }}
                >
                  <div className="relative bg-black/90 text-white text-xs px-3 py-2 rounded-lg backdrop-blur-sm max-w-[200px] shadow-lg">
                    <div className="absolute -left-1.5 top-3 w-3 h-3 bg-black/90 rotate-45" />
                    {t.common.coachInstantSave}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {showLoadMoreButton && (
          <div className="mt-6">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              aria-label="Find more places"
              aria-busy={loadingMore}
              className="w-full py-3 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/10 hover:border-[var(--accent-teal)]/40 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all flex flex-col items-center justify-center gap-1 disabled:opacity-50"
            >
              {loadingMore ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.results.searchingWiderArea}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {t.results.findMorePlaces}
                  </div>
                  {findMoreStage > 0 && FIND_MORE_HINTS[findMoreStage as FindMoreStage] && (
                    <span className="text-xs text-[var(--accent-teal)]">
                      {FIND_MORE_HINTS[findMoreStage as FindMoreStage]}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        )}
        {findMoreStage >= 5 && !loadingMore && (
          <div className="mt-6 text-center text-sm text-[var(--text-secondary)]">
            All nearby results have been found for your vibes.
          </div>
        )}

      </div>

      <div className="fixed bottom-14 left-0 right-0 p-4 bg-gradient-to-t from-[var(--bg-primary)] via-[var(--bg-primary)] to-transparent">
        <div className="max-w-2xl mx-auto">
          {isAuthenticated ? (
            <button
              onClick={handleViewTrip}
              disabled={savedPlaceIds.size === 0}
              aria-label="View saved trip"
              className="w-full py-4 rounded-lg font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)'
              }}
            >
              <Sparkles className="w-5 h-5" />
              {savedPlaceIds.size > 0
                ? `${t.results.viewTrip} · ${savedPlaceIds.size} ${savedPlaceIds.size === 1 ? 'place' : 'places'} saved`
                : t.results.viewTrip
              }
            </button>
          ) : (
            <button
              onClick={handleSaveItinerary}
              disabled={isSaving || selectedPlaceIds.size === 0}
              aria-label="Save itinerary"
              className="w-full py-4 rounded-lg font-medium text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-teal) 100%)'
              }}
            >
              <Sparkles className="w-5 h-5" />
              {isSaving
                ? t.results.saving
                : selectedPlaceIds.size > 0
                ? `Save ${selectedPlaceIds.size} ${selectedPlaceIds.size === 1 ? 'place' : 'places'}`
                : t.results.saveTrip
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
